import React, { useState } from 'react';
import { FaGithub, FaFolderPlus, FaTimes, FaMagic } from 'react-icons/fa';
import { db } from '../../../firebase';
import { doc, setDoc, collection } from 'firebase/firestore';

const AddAppModal = ({ isOpen, onClose, onAppCreated }) => {
    const [name, setName] = useState('');
    const [githubUrl, setGithubUrl] = useState('');
    const [structure, setStructure] = useState('');
    const [loading, setLoading] = useState(false);

    if (!isOpen) return null;

    const parseStructure = (text) => {
        const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
        const screens = [];
        let currentCategory = 'General';

        lines.forEach(line => {
            // Logic: If it has .tsx, it's a screen. Otherwise, it's a category.
            if (line.endsWith('.tsx') || line.endsWith('.js') || line.endsWith('.jsx')) {
                const screenName = line.replace(/\.(tsx|js|jsx)$/, '')
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/([0-9]+)/g, ' $1 ')
                    .replace(/\s+/g, ' ')
                    .trim();

                screens.push({
                    screen: screenName,
                    category: currentCategory,
                    fileName: line
                });
            } else {
                // It's a folder/category
                currentCategory = line.charAt(0).toUpperCase() + line.slice(1);
            }
        });

        return screens;
    };

    const handleCreate = async () => {
        if (!name || !githubUrl) {
            alert("Please provide App Name and GitHub URL");
            return;
        }

        setLoading(true);
        try {
            // 1. Parse URL: https://github.com/OWNER/REPO/tree/BRANCH/PATH
            // Example: https://github.com/Driveeasy-Wheelpro/driveeasy-app-frontend/tree/abhishek-dev/src/screens/
            const urlParts = githubUrl.replace(/\/$/, '').split('/');
            const treeIndex = urlParts.indexOf('tree');

            if (treeIndex === -1) {
                throw new Error("Invalid URL. Make sure it includes /tree/BRANCH/");
            }

            const owner = urlParts[3];
            const repo = urlParts[4];
            const branch = urlParts[treeIndex + 1];
            const screensPath = urlParts.slice(treeIndex + 2).join('/');
            const appId = name.toLowerCase().replace(/[^a-z0-9]/g, '-');

            // 2. Save App Configuration
            const appConfig = {
                id: appId,
                title: name,
                owner,
                repo,
                branch,
                screensPath,
                createdAt: new Date()
            };

            await setDoc(doc(db, 'tech_apps', appId), appConfig);

            // 3. Parse Structure & Seed Screens
            if (structure.trim()) {
                const parsedScreens = parseStructure(structure);
                const collectionName = `tech_${appId.replace(/-/g, '_')}_screens`;

                for (const item of parsedScreens) {
                    const docId = item.screen.toLowerCase().replace(/[^a-z0-9]/g, '_');
                    const link = `https://github.com/${owner}/${repo}/blob/${branch}/${screensPath}/${item.fileName}`;

                    await setDoc(doc(db, collectionName, docId), {
                        screen: item.screen,
                        category: item.category,
                        feStatus: 'v1-red',
                        beStatus: 'not-started',
                        apiReady: false,
                        testCasesCount: 0,
                        testCasesDone: false,
                        githubLink: link,
                        createdAt: new Date(),
                        updatedAt: new Date()
                    });
                }
            }

            alert(`Tracker for ${name} created successfully! 🚀`);
            onAppCreated(appConfig);
            onClose();
        } catch (error) {
            console.error(error);
            alert("Failed to create tracker: " + error.message);
        }
        setLoading(false);
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="bg-neutral-900 border border-neutral-800 rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-300">
                {/* Header */}
                <div className="p-6 border-b border-neutral-800 flex justify-between items-center bg-gradient-to-r from-blue-600/10 to-purple-600/10">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-blue-500/20 rounded-2xl">
                            <FaFolderPlus className="text-blue-500 text-xl" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-white">Add New App Tracker</h2>
                            <p className="text-xs text-gray-400">Specify GitHub location and screen structure</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-neutral-800 rounded-xl transition text-gray-400">
                        <FaTimes />
                    </button>
                </div>

                {/* Form */}
                <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar text-gray-200">
                    <div>
                        <label className="block text-sm font-medium mb-2 text-gray-400">App Display Name</label>
                        <input
                            type="text"
                            placeholder="e.g. Driver App V2"
                            className="w-full bg-neutral-800 border border-neutral-700 rounded-2xl px-4 py-3 focus:outline-none focus:border-blue-500 transition"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2 text-gray-400">GitHub Screens Folder URL</label>
                        <input
                            type="text"
                            placeholder="https://github.com/OWNER/REPO/tree/BRANCH/src/screens/"
                            className="w-full bg-neutral-800 border border-neutral-700 rounded-2xl px-4 py-3 focus:outline-none focus:border-blue-500 transition text-sm"
                            value={githubUrl}
                            onChange={(e) => setGithubUrl(e.target.value)}
                        />
                        <p className="text-[10px] text-gray-500 mt-2 px-1 italic">
                            Tip: Open the folder on GitHub and copy the full URL from the browser.
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-2 text-gray-400 flex justify-between items-center">
                            <span>Initial Screen Structure (Optional)</span>
                            <span className="text-[10px] bg-blue-500/10 text-blue-400 px-2 py-0.5 rounded-full border border-blue-500/20">AI Auto-Parser Active</span>
                        </label>
                        <textarea
                            rows={10}
                            placeholder="Paste folder/file list here...&#10;auth&#10;LoginScreen.tsx&#10;RegisterScreen.tsx&#10;home&#10;DashboardScreen.tsx"
                            className="w-full bg-neutral-800 border border-neutral-700 rounded-2xl px-4 py-3 focus:outline-none focus:border-blue-500 transition text-xs font-mono custom-scrollbar"
                            value={structure}
                            onChange={(e) => setStructure(e.target.value)}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 bg-neutral-950/50 border-t border-neutral-800 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-6 py-3 rounded-2xl text-gray-400 hover:bg-neutral-800 transition font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCreate}
                        disabled={loading}
                        className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-2xl font-bold shadow-lg shadow-blue-500/20 transition-all flex items-center gap-2"
                    >
                        {loading ? <FaMagic className="animate-spin" /> : <FaMagic />}
                        {loading ? "Creating..." : "Initialize Tracker"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddAppModal;
